import { theme } from 'styles/theme/theme';

/**
 * Accent palette for the media-manager editor chrome — primary buttons, badges,
 * active states and link-like toggles.
 *
 * The editor shipped with an ad-hoc blue accent (#2563eb / #4f46e5 / #6366f1)
 * that belongs to no palette. These tokens map that role onto the product
 * accent: the yellow family in `styles/theme/theme.ts` plus the `darkYellow-*`
 * scale declared in `src/index.css`.
 *
 * Two shades come from that scale rather than `theme.colors`, and they exist
 * purely for contrast: the theme yellows sit at ~2:1 against white, so they are
 * unreadable as small text and unusable behind white text. Hence `onSolid` is
 * dark ink on a yellow fill, and `ink` is the dark shade used when the accent
 * itself has to be the text color.
 */
export const editorAccent = {
  /** Primary fill — save/confirm buttons, active borders, selection rings. */
  solid: theme.colors.DarkYellow,
  /** Hover/pressed state for `solid`. Mirrors `--color-darkYellow-600`. */
  solidHover: '#b89638',
  /** Text and icons on top of `solid` — white fails contrast on yellow. */
  onSolid: theme.colors.SurfaceRaisinBlack50,
  /** Tinted surface — badges, selected slots. */
  soft: theme.colors.AccentCornsilk,
  /** Border for `soft` surfaces. */
  softBorder: theme.colors.AccentMediumChampagne,
  /** Accent-colored text on white. Mirrors `--color-darkYellow-800`. */
  ink: '#705622',
  /** Hover state for `ink`. Mirrors `--color-darkYellow-900`. */
  inkHover: '#4c3716',
  /** Accent wash for an active row/slot background. */
  wash: 'rgba(217, 178, 68, 0.16)',
  /** Lighter wash for hover. */
  washHover: 'rgba(217, 178, 68, 0.1)',
  /** Focus/selection ring around a picked card. */
  ring: 'rgba(217, 178, 68, 0.3)',
} as const;
